import { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../auth/useAuth';
import { fetchIssues, fetchIssuesSummary, updateIssue, createSubscription } from '../services/dataService';
import { SANDBOX_MODE, subscribeSandboxIssues } from '../config/sandbox';
import {
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  Layout,
  Filter,
  Eye,
  Upload,
  Download,
  MessageSquare,
  Calendar
} from 'lucide-react';
import { PageHeader } from '../components/layout';
import { Button, Card, Badge, Spinner, EmptyState, Select } from '../components/ui';
import ShopperImageExtract from '../components/ShopperImageExtract';
import { cn } from '../utils/cn';
import { buildSpotUrl } from '../utils/spotLink';
import { parseDate, subDays, subMonths } from '../utils/dateUtils';

const BYPASS_AUTH = import.meta.env.VITE_BYPASS_AUTH === 'true';

function IssuesResolution() {
  // --- AUTH ---
  const { isAuthenticated, isLoading: authLoading } = BYPASS_AUTH ? {} : useAuth();

  // --- STATE ---
  const [summaryData, setSummaryData] = useState([]);  // For dashboard cards (lightweight)
  const [detailData, setDetailData] = useState(null);  // For resolution view (current group's issues)
  const [apiError, setApiError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);  // Start true to show spinner on initial load
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [selectedRetailer, setSelectedRetailer] = useState('All');
  const [timeRange, setTimeRange] = useState('30days'); // '30days' | '3months' | 'all'
  const [resolutionFilter, setResolutionFilter] = useState('all'); // 'all' | 'pending' | 'resolved'
  const [activeView, setActiveView] = useState('dashboard');
  const [currentGroupKey, setCurrentGroupKey] = useState(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  const [resolutionState, setResolutionState] = useState(() => {
    const saved = localStorage.getItem('issuesResolution');
    return saved ? JSON.parse(saved) : {};
  });

  const [showAllEntries, setShowAllEntries] = useState(() => {
    const saved = localStorage.getItem('issuesResolution_showAll');
    // Default to true (All Entries) - user can filter to errors via button
    return saved !== null ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    localStorage.setItem('issuesResolution', JSON.stringify(resolutionState));
  }, [resolutionState]);

  useEffect(() => {
    localStorage.setItem('issuesResolution_showAll', JSON.stringify(showAllEntries));
  }, [showAllEntries]);

  useEffect(() => {
    // Wait for auth to settle before firing a request without a bearer
    if (!BYPASS_AUTH && authLoading) {
      return;
    }

    // Auth done but not authenticated - stop loading, don't fetch
    if (!BYPASS_AUTH && !isAuthenticated) {
      setIsLoading(false);
      return;
    }

    let cleanup = null;
    (async () => {
      setIsLoading(true);
      try {
        // Always fetch full summary - filtering happens in frontend
        const summary = await fetchIssuesSummary(['qc_tool'], null);
        if (summary === null) {
          setApiError(true);
        } else {
          setSummaryData(summary);
          cleanup = createSubscription(
            'swissues_summary',
            (newData) => setSummaryData(newData),
            () => fetchIssuesSummary(['qc_tool'], null)
          );
        }
      } catch (err) {
        console.error('[IssuesResolution] API load error:', err);
        setApiError(true);
      } finally {
        setIsLoading(false);
      }
    })();
    return () => { if (cleanup) cleanup(); };
  }, [authLoading, isAuthenticated]);

  // Sandbox: keep IR in sync with QC Tool pushes in real time.
  // Whenever the in-memory swissues store changes (QC pushes to ledger, or a
  // resolution PATCH mutates a row), refetch the summary and, if the user is
  // already inside a group, the detail rows for that group.
  useEffect(() => {
    if (!SANDBOX_MODE) return undefined;
    let firstCall = true;
    const unsub = subscribeSandboxIssues(async () => {
      if (firstCall) { firstCall = false; return; }
      const summary = await fetchIssuesSummary(['qc_tool'], null);
      if (summary !== null) setSummaryData(summary);
      if (activeView === 'resolution' && currentGroupKey) {
        const group = summary?.find(g => g.id === currentGroupKey);
        if (group) {
          // Inside the resolution view we always fetch Fail-only — the
          // showAllEntries toggle lives on the dashboard and changing it there
          // shouldn't surface Pass rows here.
          const issues = await fetchIssues(['qc_tool'], 'Fail', group.retailer, group.date);
          if (issues) setDetailData(issues);
        }
      }
    });
    return unsub;
  }, [activeView, currentGroupKey]);

  // Fetch detailed issues when a group is clicked
  const handleGroupClick = async (group) => {
    setCurrentGroupKey(group.id);
    setActiveView('resolution');
    setCurrentPageIndex(0);

    // Fetch detailed issues for this group — always Fail-only inside resolution view.
    setIsLoadingDetail(true);
    try {
      const issues = await fetchIssues(['qc_tool'], 'Fail', group.retailer, group.date);
      setDetailData(issues);
    } catch (err) {
      console.error('[IssuesResolution] Detail fetch error:', err);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  // Return to the dashboard AND refetch the summary so cards reflect any
  // resolution_status changes made inside the resolution view (Resolved /
  // Escalated). Without this, the dashboard stayed stale until the user
  // manually refreshed.
  const returnToDashboard = async () => {
    setActiveView('dashboard');
    try {
      const summary = await fetchIssuesSummary(['qc_tool'], null);
      if (summary !== null) setSummaryData(summary);
    } catch (err) {
      console.error('[IssuesResolution] Summary refetch error:', err);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const rows = text.split(/\r?\n/).filter(row => row.trim() !== "");
      const headers = rows[0].split(',').map(h => h.trim());

      const jsonData = rows.slice(1).map((row, index) => {
        const values = [];
        let current = '';
        let inQuotes = false;

        for (let i = 0; i < row.length; i++) {
          const char = row[i];
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            values.push(current.trim().replace(/^"|"$/g, ''));
            current = '';
          } else {
            current += char;
          }
        }
        values.push(current.trim().replace(/^"|"$/g, ''));

        const obj = {};
        headers.forEach((header, i) => {
          obj[header] = values[i] || '';
        });

        return {
          id: `issue-${index}`,
          retailer: obj["Retailer"],
          date: obj["Date"],
          page: parseInt(obj["Page"]) || 0,
          level: obj["Level"],
          spotId: obj["Spot ID"],
          shopperId: obj["Shopper ID"],
          upc: obj["UPC"],
          description: obj["Description"],
          issueType: obj["Issue Type"],
          status: obj["Status"],
          suggestedUPC: obj["Suggested UPC/Spot"],
          observation: obj["Observation"],
          isContext: obj["Level"] && obj["Level"].includes("(Context)")
        };
      });

      const validData = jsonData.filter(item => item.retailer);

      // Generate summary from CSV data
      const summaryMap = {};
      validData.forEach(item => {
        const key = `${item.retailer}-${item.date}`;
        if (!summaryMap[key]) {
          summaryMap[key] = {
            id: key,
            retailer: item.retailer,
            date: item.date,
            totalIssues: 0,
            errorCount: 0,
            passCount: 0,
            pageCount: new Set()
          };
        }
        summaryMap[key].totalIssues++;
        summaryMap[key].pageCount.add(item.page);
        if (item.status === 'Fail') {
          summaryMap[key].errorCount++;
        } else {
          summaryMap[key].passCount++;
        }
      });

      // Convert pageCount Set to number
      const summaryArray = Object.values(summaryMap).map(s => ({
        ...s,
        pageCount: s.pageCount.size
      }));

      setSummaryData(summaryArray);
      // Store full data as detail data for immediate use
      setDetailData(validData);
    };
    reader.readAsText(file);
  };

  // Process detailData into grouped structure for resolution view
  const currentGroupDetail = useMemo(() => {
    if (!detailData || detailData.length === 0) return null;

    const pages = {};
    detailData.forEach(item => {
      if (!pages[item.page]) {
        pages[item.page] = {
          pageNumber: item.page,
          pageIssues: [],
          spotIssues: [],
          shopperIssues: [],
          allSpotProducts: {}
        };
      }

      const level = (item.level || '').trim();

      if (level === 'Page') {
        pages[item.page].pageIssues.push(item);
      } else if (level === 'Shopper') {
        pages[item.page].shopperIssues.push(item);
      } else if (level === 'Spot') {
        // Pass spot rows are "audited, no spot-level issue" markers emitted by
        // the QC Tool for context; they aren't issues. Skip them so they don't
        // get consolidated into a phantom "${spotId}-Other" card on top of the
        // real product-level error for the same spot.
        if (item.status === 'Fail') {
          pages[item.page].spotIssues.push(item);
        }
      } else if (level.startsWith('Product/UPC')) {
        const spotId = item.spotId;
        if (spotId) {
          if (!pages[item.page].allSpotProducts[spotId]) {
            pages[item.page].allSpotProducts[spotId] = [];
          }
          pages[item.page].allSpotProducts[spotId].push({
            upc: item.upc,
            description: item.description,
            issueType: item.issueType,
            hasError: !item.isContext && item.issueType,
            suggestedUPC: item.suggestedUPC,
            observation: item.observation,
            status: item.status,
            originalItem: item
          });
        }

        if (!item.isContext) {
          pages[item.page].spotIssues.push(item);
        }
      }
    });

    // Get retailer/date from first item
    const firstItem = detailData[0];
    return {
      id: currentGroupKey,
      retailer: firstItem.retailer,
      date: firstItem.date,
      pages
    };
  }, [detailData, currentGroupKey]);

  // Dashboard uses summaryData directly - no client-side grouping needed
  const retailers = useMemo(() => ['All', ...new Set(summaryData.map(g => g.retailer))], [summaryData]);

  // Time-based filtering and sorting
  const filteredByTime = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    return summaryData
      .filter(g => {
        // Filter by latest IR activity (MAX created_at) so a freshly-pushed
        // shopper appears in "Last 30 Days" regardless of how old the CSV's
        // start date is. Fall back to the shopper start date when activity
        // is unknown (e.g. lambda not yet upgraded with latest_created_at).
        const activity = parseDate(g.latestCreatedAt) || parseDate(g.date);
        if (!activity) return timeRange === 'all';

        switch (timeRange) {
          case '30days':
            // "Last 30 Days" = today + 29 prior days. subDays(now, 29) so the
            // window is exactly 30 calendar days and today is always included.
            return activity >= subDays(now, 29);
          case '3months':
            return activity >= subMonths(now, 3);
          default:
            return true;
        }
      })
      .sort((a, b) => {
        const aa = parseDate(a.latestCreatedAt) || parseDate(a.date);
        const bb = parseDate(b.latestCreatedAt) || parseDate(b.date);
        if (!aa && !bb) return 0;
        if (!aa) return 1;
        if (!bb) return -1;
        return bb - aa; // Newest activity first
      });
  }, [summaryData, timeRange]);

  // Apply retailer filter, resolution-status filter, and "Show Errors Only" filter on top of time filter
  const filteredGroups = filteredByTime.filter(g => {
    const matchesRetailer = selectedRetailer === 'All' || g.retailer === selectedRetailer;
    // When "Show Errors Only" is active, only show groups with pending errors
    const hasErrors = showAllEntries || (g.errorCount || 0) > 0;
    const errorCount = g.errorCount || 0;
    const resolvedErrorCount = g.resolvedErrorCount || 0;
    const pendingIssues = Math.max(0, errorCount - resolvedErrorCount);
    let matchesResolution = true;
    if (resolutionFilter === 'pending') {
      matchesResolution = pendingIssues > 0;
    } else if (resolutionFilter === 'resolved') {
      matchesResolution = errorCount > 0 && pendingIssues === 0;
    }
    return matchesRetailer && hasErrors && matchesResolution;
  });

  // Stats for dashboard cards - uses summary data from backend
  const getGroupStats = (group) => {
    const totalIssues = group.totalIssues || 0;
    const errorCount = group.errorCount || 0;           // Fail status from QC
    const passCount = group.passCount || 0;             // Pass/NULL status from QC (no issues)
    const resolvedErrorCount = group.resolvedErrorCount || 0;  // Fail that were resolved

    // Pass (GREEN): entries that passed QC (NULL + Pass) - no action needed
    // Pending (RED): Fail entries not yet resolved
    // Resolved (YELLOW): Fail entries that have been resolved
    const pendingIssues = Math.max(0, errorCount - resolvedErrorCount);

    return {
      totalIssues,
      errorCount,
      passCount,              // GREEN - passed QC
      resolvedIssues: resolvedErrorCount,  // YELLOW - errors that were resolved
      pendingIssues,          // RED - errors still pending
      pageCount: group.pageCount || 0,
      pageErrorCount: group.pageErrorCount || 0,
      spotErrorCount: group.spotErrorCount || 0,
      productErrorCount: group.productErrorCount || 0,
      percent: totalIssues ? Math.round(((passCount + resolvedErrorCount) / totalIssues) * 100) : 0
    };
  };

  // Resolve the real DB ids to PATCH from a click target. For ordinary cards
  // the click passes a real id and we just PATCH it. For consolidated cards
  // (one card representing N rows that share spotId + issueType), we PATCH
  // every underlying id so the UI count and DB state stay in sync.
  const resolveTargetIds = (issueId, underlyingIds) => {
    if (Array.isArray(underlyingIds) && underlyingIds.length > 0) return underlyingIds;
    const issueRecord = detailData?.find(item => item.id === issueId);
    return issueRecord?.id ? [issueRecord.id] : [];
  };

  // Local-only update: writes to React state on every keystroke. No PATCH.
  // Bound to onChange of the text inputs so typing stays snappy and we don't
  // hammer the ledger per character.
  const updateResolutionLocal = (issueId, field, value) => {
    setResolutionState(prev => ({
      ...prev,
      [issueId]: {
        ...prev[issueId],
        [field]: value,
        status: prev[issueId]?.status || 'Pending',
        groupId: currentGroupKey,
      },
    }));
  };

  // Commit: fires exactly one PATCH per underlying id at the moment the user
  // signals they're done editing (blur / Enter). Skips the write when the new
  // value matches what's already on the record.
  const commitResolution = (issueId, field, value, underlyingIds = null) => {
    const fieldMap = { resolution: 'resolution_notes', suggestedUPC: 'suggested_upc_spot' };
    const apiField = fieldMap[field];
    if (!apiField) return;
    const existing = detailData?.find(item => item.id === issueId);
    const previousValue = field === 'resolution'
      ? (existing?.resolutionNotes ?? '')
      : (existing?.suggestedUPC ?? '');
    if ((value ?? '') === previousValue) return;
    for (const id of resolveTargetIds(issueId, underlyingIds)) {
      updateIssue(id, { [apiField]: value });
    }
  };

  const updateStatus = (issueId, status, underlyingIds = null) => {
    setResolutionState(prev => ({
      ...prev,
      [issueId]: { ...prev[issueId], status, groupId: currentGroupKey }
    }));
    for (const id of resolveTargetIds(issueId, underlyingIds)) {
      updateIssue(id, { resolution_status: status });
    }
  };

  // Count hidden groups when "Show Errors Only" is active
  const hiddenCount = showAllEntries ? 0 : filteredByTime.filter(g => {
    const matchesRetailer = selectedRetailer === 'All' || g.retailer === selectedRetailer;
    return matchesRetailer && (g.errorCount || 0) === 0;
  }).length;

  const downloadReport = () => {
    const reportRows = [["Retailer", "Date", "Page", "Level", "Spot ID", "UPC", "Description", "Issue Type", "Original Status", "Resolution Status", "Suggested UPC/Spot", "Resolution Notes"]];

    // Use detailData if available (resolution view), otherwise report is empty
    const dataToExport = detailData || [];
    dataToExport.forEach(item => {
      const resState = resolutionState[item.id];
      reportRows.push([
        item.retailer,
        item.date,
        item.page,
        item.level,
        item.spotId,
        item.upc,
        item.description,
        item.issueType,
        item.status,
        resState?.status || "Pending",
        resState?.suggestedUPC || item.suggestedUPC || "",
        resState?.resolution || item.observation || ""
      ]);
    });

    const csvContent = reportRows.map(e => e.map(field => `"${field}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `QC_Resolution_Report_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // DASHBOARD VIEW
  if (activeView === 'dashboard') {
    return (
      <div className="space-y-6">
        <PageHeader
          title="QC Issue Resolution Dashboard"
          description="Review and resolve data quality issues"
        />

        {(authLoading || isLoading) ? (
          <Card className="flex items-center justify-center h-64">
            <Spinner size="lg" label={authLoading ? "Authenticating..." : "Loading issues..."} />
          </Card>
        ) : summaryData.length === 0 ? (
          <Card className="border-2 border-dashed">
            <EmptyState
              icon={<Layout />}
              title={apiError ? "API unavailable" : "No issues found"}
              description={apiError ? "Upload a QC CSV report as fallback." : "Upload a QC CSV report to load data."}
            />
          </Card>
        ) : (
          <>
            {/* Filters */}
            <Card className="p-4">
              <div className="flex gap-4 flex-wrap items-center">
                {/* Retailer Filter */}
                <div className="flex items-center gap-2">
                  <Filter size={18} className="text-neutral-400" />
                  <Select
                    value={selectedRetailer}
                    onChange={(e) => setSelectedRetailer(e.target.value)}
                    options={retailers.map(r => ({ value: r, label: r }))}
                  />
                </div>

                {/* Time Range Filter */}
                <div className="flex items-center gap-1 border-l border-neutral-200 pl-4">
                  <Calendar size={16} className="text-neutral-400 mr-1" />
                  {[
                    { key: '30days', label: 'Last 30 Days' },
                    { key: '3months', label: 'Last 3 Months' },
                    { key: 'all', label: 'All Time' }
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => setTimeRange(key)}
                      className={cn(
                        "px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                        timeRange === key
                          ? "bg-primary-600 text-white"
                          : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {/* Resolution Status Filter */}
                <div className="flex items-center gap-1 border-l border-neutral-200 pl-4">
                  <CheckCircle2 size={16} className="text-neutral-400 mr-1" />
                  {[
                    { key: 'all', label: 'All' },
                    { key: 'pending', label: 'Pending' },
                    { key: 'resolved', label: 'Resolved' }
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => setResolutionFilter(key)}
                      className={cn(
                        "px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                        resolutionFilter === key
                          ? "bg-primary-600 text-white"
                          : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {/* Show All/Errors Toggle */}
                <div className="flex items-center gap-2 ml-auto">
                  <button
                    onClick={() => setShowAllEntries(!showAllEntries)}
                    className={cn(
                      "inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                      "border-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2",
                      !showAllEntries
                        ? "bg-primary-600 text-white border-primary-600 hover:bg-primary-700"
                        : "bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-50"
                    )}
                  >
                    <Filter size={14} />
                    {showAllEntries ? 'Show Errors Only' : 'Show All Entries'}
                  </button>
                  {!showAllEntries && hiddenCount > 0 && (
                    <span className="text-xs text-neutral-500">
                      {hiddenCount} hidden
                    </span>
                  )}
                </div>
              </div>
            </Card>

            {/* Results Count */}
            <div className="flex items-center justify-between text-sm text-neutral-500">
              <span>
                Showing <span className="font-semibold text-neutral-700">{filteredGroups.length}</span> shopper{filteredGroups.length !== 1 ? 's' : ''}
                {selectedRetailer !== 'All' && <span> for {selectedRetailer}</span>}
                {timeRange !== 'all' && (
                  <span> from {timeRange === '30days' ? 'the last 30 days' : 'last 3 months'}</span>
                )}
                {resolutionFilter !== 'all' && (
                  <span> ({resolutionFilter === 'pending' ? 'with pending errors' : 'fully resolved'})</span>
                )}
              </span>
              {summaryData.length > filteredGroups.length && (
                <span className="text-xs">
                  {summaryData.length - filteredGroups.length} hidden by filters
                </span>
              )}
            </div>

            {/* Issue Group Cards */}
            {filteredGroups.length === 0 ? (
              <Card className="border-2 border-dashed">
                <EmptyState
                  icon={<AlertCircle />}
                  title="No shoppers found"
                  description={
                    resolutionFilter !== 'all'
                      ? `No shoppers ${resolutionFilter === 'pending' ? 'with pending errors' : 'fully resolved'} in the current filters. Try expanding the filters.`
                      : timeRange !== 'all'
                        ? `No shoppers found for ${timeRange === '30days' ? 'the last 30 days' : 'the last 3 months'}. Try expanding the time range.`
                        : showAllEntries
                          ? "No issues to display."
                          : "No errors found in the current filters. Toggle to view all entries."
                  }
                  action={
                    resolutionFilter !== 'all' ? (
                      <Button onClick={() => setResolutionFilter('all')} leftIcon={<CheckCircle2 size={18} />}>
                        Show All Statuses
                      </Button>
                    ) : timeRange !== 'all' ? (
                      <Button onClick={() => setTimeRange('all')} leftIcon={<Calendar size={18} />}>
                        View All Time
                      </Button>
                    ) : !showAllEntries ? (
                      <Button onClick={() => setShowAllEntries(true)} leftIcon={<Eye size={18} />}>
                        Show All Entries
                      </Button>
                    ) : null
                  }
                />
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredGroups.map((group) => {
                const stats = getGroupStats(group);
                return (
                  <Card key={group.id} hoverable className="overflow-hidden">
                    <div className="p-5">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="font-bold text-lg text-neutral-900">{group.retailer}</h3>
                          <p className="text-xs text-neutral-500">{group.date}</p>
                        </div>
                        <div className="flex flex-col gap-1 text-right">
                          {stats.pendingIssues > 0 && (
                            <Badge variant="error" className="text-[10px]">
                              <AlertCircle size={12} /> {stats.pendingIssues} PENDING
                            </Badge>
                          )}
                          {stats.resolvedIssues > 0 && (
                            <Badge variant="warning" className="text-[10px]">
                              <CheckCircle2 size={12} /> {stats.resolvedIssues} RESOLVED
                            </Badge>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between text-xs font-semibold">
                          <span className="text-neutral-400">ERROR RESOLUTION</span>
                          <span className="text-neutral-700">{stats.resolvedIssues} / {stats.errorCount} Errors</span>
                        </div>
                        <div className="w-full bg-neutral-100 h-2 rounded-full overflow-hidden">
                          <div
                            className={cn(
                              "h-full transition-all duration-500",
                              stats.pendingIssues === 0 ? 'bg-success-500' : 'bg-primary-500'
                            )}
                            style={{ width: `${stats.errorCount ? Math.round((stats.resolvedIssues / stats.errorCount) * 100) : 100}%` }}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                        <div className="bg-success-50 rounded p-2">
                          <div className="text-xl font-bold text-success-600">{stats.passCount}</div>
                          <div className="text-[9px] text-success-700 font-semibold uppercase">Pass</div>
                        </div>
                        <div className="bg-error-50 rounded p-2">
                          <div className="text-xl font-bold text-error-600">{stats.pendingIssues}</div>
                          <div className="text-[9px] text-error-700 font-semibold uppercase">Pending</div>
                        </div>
                        <div className="bg-warning-50 rounded p-2">
                          <div className="text-xl font-bold text-warning-600">{stats.resolvedIssues}</div>
                          <div className="text-[9px] text-warning-700 font-semibold uppercase">Resolved</div>
                        </div>
                      </div>

                      {stats.errorCount > 0 && (
                        <div className="flex items-center justify-between text-[10px] mb-4 px-1">
                          <span className="text-neutral-400 font-semibold uppercase tracking-wider">Errors by Level</span>
                          <span className="font-semibold text-neutral-600">
                            <span className="text-error-600">{stats.pageErrorCount}</span> Page
                            <span className="text-neutral-300 mx-1.5">·</span>
                            <span className="text-error-600">{stats.spotErrorCount}</span> Spot
                            <span className="text-neutral-300 mx-1.5">·</span>
                            <span className="text-error-600">{stats.productErrorCount}</span> Product
                          </span>
                        </div>
                      )}

                      <Button
                        onClick={() => handleGroupClick(group)}
                        className="w-full"
                        leftIcon={<Eye size={18} />}
                      >
                        {stats.pendingIssues === 0 ? 'View Details' : stats.resolvedIssues === 0 ? 'Start Resolution' : 'Continue Resolution'}
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>
            )}
          </>
        )}
      </div>
    );
  }

  // RESOLUTION VIEW - Loading state for detail fetch
  if (isLoadingDetail) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="p-8 text-center">
          <Spinner size="lg" label="Loading issue details..." />
        </Card>
      </div>
    );
  }

  // Use currentGroupDetail (processed from detailData)
  const currentGroup = currentGroupDetail;

  if (!currentGroup) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="p-8 text-center">
          <AlertCircle size={48} className="text-neutral-300 mx-auto mb-4" />
          <p className="text-neutral-600 mb-4">
            No errors found in this group, or the group could not be loaded.
          </p>
          <div className="flex gap-2 justify-center">
            <Button onClick={returnToDashboard}>
              Return to Dashboard
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const pageNumbers = Object.keys(currentGroup.pages || {}).map(Number).sort((a, b) => a - b);

  if (pageNumbers.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="p-8 text-center">
          <AlertCircle size={48} className="text-neutral-300 mx-auto mb-4" />
          <p className="text-neutral-600 mb-4">No pages found for this group.</p>
          <Button onClick={returnToDashboard}>
            Return to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  const pageNum = pageNumbers[currentPageIndex];
  const page = currentGroup.pages[pageNum];

  if (!page) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="p-8 text-center">
          <AlertCircle size={48} className="text-neutral-300 mx-auto mb-4" />
          <p className="text-neutral-600 mb-4">Page data not found.</p>
          <Button onClick={returnToDashboard}>
            Return to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  // Spot / Product counts for the current page header. Spots are the union
  // of spotIds seen in spot-level rows and in the product-context map (a spot
  // can exist with only a Spot-level row and no products attached).
  const pageSpotIds = new Set([
    ...(page.spotIssues || []).map(i => i.spotId).filter(Boolean),
    ...Object.keys(page.allSpotProducts || {}),
  ]);
  const pageSpotCount = pageSpotIds.size;
  const pageProductCount = Object.values(page.allSpotProducts || {})
    .reduce((sum, arr) => sum + arr.length, 0);

  const consolidatedSpotIssues = {};
  (page.spotIssues || []).forEach(issue => {
    const normalizedIssueType = issue.issueType || 'Other';
    const hasValidSpotId = issue.spotId && issue.spotId.trim() !== '';
    const key = hasValidSpotId ? `${issue.spotId}-${normalizedIssueType}` : issue.id;

    if (!consolidatedSpotIssues[key]) {
      const spotProducts = page.allSpotProducts?.[issue.spotId] || [];
      consolidatedSpotIssues[key] = {
        ...issue,
        issueType: normalizedIssueType,
        products: spotProducts.length > 0 ? spotProducts : [{
          upc: issue.upc,
          description: issue.description,
          hasError: Boolean(issue.issueType)
        }],
        consolidatedId: key,
        _underlyingIds: [issue.id],
      };
    } else {
      consolidatedSpotIssues[key]._underlyingIds.push(issue.id);
    }
  });

  const allIssues = [
    ...(page.pageIssues || []).map(issue => ({ ...issue, issueCategory: 'page' })),
    ...Object.values(consolidatedSpotIssues).map(issue => ({ ...issue, issueCategory: 'spot' })),
    ...(page.shopperIssues || []).map(issue => ({ ...issue, issueCategory: 'shopper' }))
  ];

  const issuesByType = {};
  allIssues.forEach(issue => {
    const type = issue.issueType || 'Other';
    if (!issuesByType[type]) issuesByType[type] = [];
    issuesByType[type].push(issue);
  });

  // RESOLUTION VIEW
  return (
    <div className="flex h-[calc(100vh-4rem)] -m-6 bg-neutral-100">
      {/* Sidebar */}
      <aside className="w-80 bg-white border-r border-neutral-200 flex flex-col shadow-lg z-10">
        <div className="p-4 border-b border-neutral-200 flex items-center justify-between bg-neutral-900 text-white">
          <Button
            variant="ghost"
            size="sm"
            onClick={returnToDashboard}
            className="text-white hover:text-primary-400"
            leftIcon={<ChevronLeft size={20} />}
          >
            Back
          </Button>
          <div className="text-center">
            <h2 className="font-bold text-xs uppercase tracking-widest">{currentGroup.retailer}</h2>
            <p className="text-[10px] opacity-60">Page {pageNum} of {pageNumbers.length}</p>
          </div>
          <div className="w-16" />
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {pageNumbers.map((p, idx) => {
            const pg = currentGroup.pages[p];
            if (!pg) return null;

            const consolidatedSpots = {};
            (pg.spotIssues || []).forEach(issue => {
              const hasValidSpotId = issue.spotId && issue.spotId.trim() !== '';
              const key = hasValidSpotId ? `${issue.spotId}-${issue.issueType || 'Other'}` : issue.id;
              if (!consolidatedSpots[key]) {
                consolidatedSpots[key] = { ...issue, consolidatedId: key };
              }
            });

            const pgIssues = [
              ...(pg.pageIssues || []),
              ...Object.values(consolidatedSpots),
              ...(pg.shopperIssues || [])
            ];
            const resolved = pgIssues.filter(i => resolutionState[i.consolidatedId || i.id]?.status === 'Resolved').length;
            const escalated = pgIssues.filter(i => resolutionState[i.consolidatedId || i.id]?.status === 'Escalated').length;
            const pending = pgIssues.length - resolved - escalated;

            return (
              <button
                key={p}
                onClick={() => setCurrentPageIndex(idx)}
                className={cn(
                  "w-full flex items-center gap-3 p-2.5 rounded-lg transition-all border",
                  currentPageIndex === idx
                    ? 'bg-primary-50 border-primary-200 text-primary-700 shadow-sm'
                    : 'bg-white border-neutral-100 text-neutral-500 hover:bg-neutral-50'
                )}
              >
                <div className={cn(
                  "w-7 h-7 rounded flex items-center justify-center text-[10px] font-bold",
                  currentPageIndex === idx ? 'bg-primary-600 text-white' : 'bg-neutral-100'
                )}>
                  {p}
                </div>
                <div className="flex-1 text-left">
                  <div className="text-sm font-semibold">Page {p}</div>
                  <div className="text-[9px] text-neutral-400">{pgIssues.length} issues</div>
                </div>
                <div className="flex flex-col gap-0.5 text-[9px] font-bold">
                  {resolved > 0 && <span className="text-success-600">{resolved}✓</span>}
                  {escalated > 0 && <span className="text-error-600">{escalated}⚠</span>}
                  {pending > 0 && <span className="text-warning-600">{pending}⋯</span>}
                </div>
              </button>
            );
          })}
        </div>

        <div className="p-4 bg-neutral-50 border-t border-neutral-200">
          <div className="text-xs text-neutral-600 mb-3 space-y-1">
            <div className="flex justify-between">
              <span>Total Issues:</span>
              <span className="font-bold">{allIssues.length}</span>
            </div>
            <div className="flex justify-between text-success-600">
              <span>Resolved:</span>
              <span className="font-bold">{allIssues.filter(i => resolutionState[i.consolidatedId || i.id]?.status === 'Resolved').length}</span>
            </div>
            <div className="flex justify-between text-warning-600">
              <span>Pending:</span>
              <span className="font-bold">{allIssues.filter(i => !resolutionState[i.consolidatedId || i.id]?.status || resolutionState[i.consolidatedId || i.id]?.status === 'Pending').length}</span>
            </div>
            <div className="flex justify-between text-error-600">
              <span>Escalated:</span>
              <span className="font-bold">{allIssues.filter(i => resolutionState[i.consolidatedId || i.id]?.status === 'Escalated').length}</span>
            </div>
          </div>
          <Button
            onClick={() => setCurrentPageIndex(prev => Math.min(pageNumbers.length - 1, prev + 1))}
            disabled={currentPageIndex >= pageNumbers.length - 1}
            className="w-full"
            size="sm"
          >
            Next Page
          </Button>
        </div>
      </aside>

      {/* Main Area */}
      <main className="flex-1 overflow-hidden flex flex-col">
        <header className="h-14 bg-white border-b border-neutral-200 flex items-center justify-between px-6 shadow-sm">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-3 text-sm">
              <span className="text-neutral-400 font-bold uppercase tracking-wider">Page {pageNum}</span>
              <span className="text-neutral-300">•</span>
              <span className="text-neutral-700 font-semibold">
                {pageSpotCount} {pageSpotCount === 1 ? 'Spot' : 'Spots'}
              </span>
              <span className="text-neutral-300">•</span>
              <span className="text-neutral-700 font-semibold">
                {pageProductCount} {pageProductCount === 1 ? 'Product' : 'Products'}
              </span>
              <span className="text-neutral-300">•</span>
              <span className="text-neutral-700 font-semibold">
                {allIssues.length} {allIssues.length === 1 ? 'Error' : 'Errors'}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentPageIndex(prev => Math.max(0, prev - 1))}
              disabled={currentPageIndex === 0}
            >
              <ChevronLeft size={20} />
            </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPageIndex(prev => Math.min(pageNumbers.length - 1, prev + 1))}
                disabled={currentPageIndex >= pageNumbers.length - 1}
              >
                <ChevronRight size={20} />
              </Button>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Page Level Issues — only render Fail rows. Pass page rows are
              "audited, no error" markers, not issues, and were rendering as
              cards titled "Page Level Issues" with "Original Status: Pass". */}
          {page.pageIssues.filter(i => i.status === 'Fail').length > 0 && (
            <Card className="border-2 border-error-200 p-5">
              <div className="flex items-center gap-2 mb-4">
                <AlertCircle className="text-error-500" size={20} />
                <h3 className="font-bold text-error-700 uppercase text-sm">Page Level Issues</h3>
              </div>
              <div className="space-y-4">
                {page.pageIssues.filter(i => i.status === 'Fail').map((issue) => {
                  const resState = resolutionState[issue.id] || {};
                  return (
                    <div key={issue.id} className="border border-neutral-200 rounded-lg p-4 bg-neutral-50">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="error" className="text-[10px]">
                              {issue.issueType || 'Page Issue'}
                            </Badge>
                            <span className="text-[10px] text-neutral-400">Original Status: {issue.status}</span>
                          </div>
                          {issue.observation && (
                            <div className="flex gap-2 p-3 mb-3 bg-warning-50 border-l-4 border-warning-500 rounded-r">
                              <MessageSquare className="h-4 w-4 text-warning-700 flex-shrink-0 mt-0.5" />
                              <p className="text-sm text-warning-900 font-medium leading-snug">
                                {issue.observation}
                              </p>
                            </div>
                          )}
                          {issue.shopperId && (
                            <div className="text-[10px] font-black mb-2">
                              <span className="text-neutral-400">SHOPPER ID: </span>
                              <a
                                href={`https://abconsultingpr.com/admin/sw1dbtables/sw1shopper/${issue.shopperId}/change/`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-error-600 hover:text-error-800 hover:underline"
                              >
                                {issue.shopperId}
                              </a>
                            </div>
                          )}
                          {/* Missing Spot Image Extract */}
                          {issue.meta?.rect && issue.shopperImageUrl && (
                            <ShopperImageExtract
                              imageUrl={issue.shopperImageUrl}
                              rect={issue.meta.rect}
                              metadata={issue.meta}
                              pageNumber={issue.page}
                            />
                          )}
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => updateStatus(issue.id, 'Resolved')}
                            className={cn(
                              "px-3 py-1 rounded text-xs font-bold transition-colors",
                              resState.status === 'Resolved'
                                ? 'bg-success-500 text-white'
                                : 'bg-neutral-100 text-neutral-400 hover:bg-success-50 hover:text-success-600'
                            )}
                          >
                            Resolved
                          </button>
                          <button
                            onClick={() => updateStatus(issue.id, 'Escalated')}
                            className={cn(
                              "px-3 py-1 rounded text-xs font-bold transition-colors",
                              resState.status === 'Escalated'
                                ? 'bg-error-500 text-white'
                                : 'bg-neutral-100 text-neutral-400 hover:bg-error-50 hover:text-error-600'
                            )}
                          >
                            Escalate
                          </button>
                        </div>
                      </div>
                      <textarea
                        className="w-full text-xs border border-neutral-200 rounded p-2 h-16 outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                        placeholder="Add resolution notes..."
                        value={resState.resolution || ''}
                        onChange={(e) => updateResolutionLocal(issue.id, 'resolution', e.target.value)}
                        onBlur={(e) => commitResolution(issue.id, 'resolution', e.target.value)}
                      />
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Shopper Level Issues — only Fail rows, same rationale as Page. */}
          {page.shopperIssues.filter(i => i.status === 'Fail').length > 0 && (
            <Card className="border-2 border-purple-200 p-5">
              <div className="flex items-center gap-2 mb-4">
                <AlertCircle className="text-purple-500" size={20} />
                <h3 className="font-bold text-purple-700 uppercase text-sm">Shopper Level Issues</h3>
              </div>
              <div className="space-y-4">
                {page.shopperIssues.filter(i => i.status === 'Fail').map((issue) => {
                  const resState = resolutionState[issue.id] || {};
                  return (
                    <div key={issue.id} className="border border-neutral-200 rounded-lg p-4 bg-neutral-50">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className="text-[10px] bg-purple-100 text-purple-700">
                              {issue.issueType || 'Shopper Issue'}
                            </Badge>
                            <span className="text-[10px] text-neutral-400">Original Status: {issue.status}</span>
                          </div>
                          {issue.observation && (
                            <div className="flex gap-2 p-3 mb-3 bg-warning-50 border-l-4 border-warning-500 rounded-r">
                              <MessageSquare className="h-4 w-4 text-warning-700 flex-shrink-0 mt-0.5" />
                              <p className="text-sm text-warning-900 font-medium leading-snug">
                                {issue.observation}
                              </p>
                            </div>
                          )}
                          {issue.shopperId && (
                            <div className="text-[10px] font-black mb-2">
                              <span className="text-neutral-400">SHOPPER ID: </span>
                              <a
                                href={`https://abconsultingpr.com/admin/sw1dbtables/sw1shopper/${issue.shopperId}/change/`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-purple-600 hover:text-purple-800 hover:underline"
                              >
                                {issue.shopperId}
                              </a>
                            </div>
                          )}
                          {issue.description && (
                            <div className="text-sm font-semibold text-neutral-700 mb-1">{issue.description}</div>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => updateStatus(issue.id, 'Resolved')}
                            className={cn(
                              "px-3 py-1 rounded text-xs font-bold transition-colors",
                              resState.status === 'Resolved'
                                ? 'bg-success-500 text-white'
                                : 'bg-neutral-100 text-neutral-400 hover:bg-success-50 hover:text-success-600'
                            )}
                          >
                            Resolved
                          </button>
                          <button
                            onClick={() => updateStatus(issue.id, 'Escalated')}
                            className={cn(
                              "px-3 py-1 rounded text-xs font-bold transition-colors",
                              resState.status === 'Escalated'
                                ? 'bg-error-500 text-white'
                                : 'bg-neutral-100 text-neutral-400 hover:bg-error-50 hover:text-error-600'
                            )}
                          >
                            Escalate
                          </button>
                        </div>
                      </div>
                      <textarea
                        className="w-full text-xs border border-neutral-200 rounded p-2 h-16 outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                        placeholder="Add resolution notes..."
                        value={resState.resolution || ''}
                        onChange={(e) => updateResolutionLocal(issue.id, 'resolution', e.target.value)}
                        onBlur={(e) => commitResolution(issue.id, 'resolution', e.target.value)}
                      />
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Spot Level Issues - Grouped by Type */}
          {Object.keys(issuesByType).filter(type => issuesByType[type].some(i => i.issueCategory === 'spot')).map(issueType => {
            const typeIssues = issuesByType[issueType].filter(i => i.issueCategory === 'spot');
            if (typeIssues.length === 0) return null;

            return (
              <Card key={issueType} className="p-5">
                <div className="flex items-center gap-2 mb-4">
                  <AlertCircle className="text-warning-500" size={18} />
                  <h3 className="font-bold text-neutral-700 uppercase text-sm">{issueType}</h3>
                  <span className="ml-auto text-xs text-neutral-400">{typeIssues.length} issues</span>
                </div>
                <div className="grid gap-4">
                  {typeIssues.map((issue) => {
                    const issueId = issue.consolidatedId || issue.id;
                    const resState = resolutionState[issueId] || {};
                    return (
                      <div key={issueId} className="border border-neutral-200 rounded-lg p-4 hover:border-primary-300 transition-colors">
                        <div className="flex items-start gap-4">
                          <div className="flex-1">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                {issue.spotId && (
                                  <div className="text-[10px] font-black mb-2">
                                    <span className="text-neutral-400">SPOT ID: </span>
                                    <a
                                      href={buildSpotUrl(issue.spotId) ?? undefined}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-primary-600 hover:text-primary-800 hover:underline"
                                    >
                                      {issue.spotId}
                                    </a>
                                  </div>
                                )}
                                {issue.observation && (
                                  <div className="flex gap-2 p-3 mb-3 bg-warning-50 border-l-4 border-warning-500 rounded-r">
                                    <MessageSquare className="h-4 w-4 text-warning-700 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-warning-900 font-medium leading-snug">
                                      {issue.observation}
                                    </p>
                                  </div>
                                )}
                                {issue.products && issue.products.length > 0 ? (
                                  <div className="space-y-1 mb-2">
                                    <div className="text-[10px] font-bold text-neutral-500 uppercase mb-1">
                                      Products on this spot ({issue.products.length}):
                                    </div>
                                    {issue.products.map((product, idx) => (
                                      <div key={idx} className={cn(
                                        "pl-3 border-l-2",
                                        product.hasError ? 'border-error-400 bg-error-50/30' : 'border-neutral-200'
                                      )}>
                                        {product.hasError === false && (
                                          <span className="text-[9px] text-neutral-400 font-semibold uppercase">Context - No Error</span>
                                        )}
                                        {product.description && (
                                          <div className={cn(
                                            "text-xs font-semibold",
                                            product.hasError ? 'text-error-700' : 'text-neutral-600'
                                          )}>
                                            {product.description}
                                          </div>
                                        )}
                                        {product.upc && (
                                          <div className="text-xs">
                                            <span className="text-neutral-600">UPC: </span>
                                            <a
                                              href={`https://us-east-1.online.tableau.com/#/site/reesa/views/BuscadorREESA2025_4/HistorialParticipaciondelProducto?:iid=2&Customername=${encodeURIComponent(issue.retailer)}&SelectedProduct=${encodeURIComponent(product.upc)}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-primary-600 hover:text-primary-800 hover:underline"
                                            >
                                              {product.upc}
                                            </a>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <>
                                    {issue.description && (
                                      <div className="text-sm font-semibold text-neutral-700">{issue.description}</div>
                                    )}
                                    {issue.upc && (
                                      <div className="text-xs mt-1">
                                        <span className="text-neutral-600">UPC: </span>
                                        <a
                                          href={`https://us-east-1.online.tableau.com/#/site/reesa/views/BuscadorREESA2025_4/HistorialParticipaciondelProducto?:iid=2&Customername=${encodeURIComponent(issue.retailer)}&SelectedProduct=${encodeURIComponent(issue.upc)}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-primary-600 hover:text-primary-800 hover:underline"
                                        >
                                          {issue.upc}
                                        </a>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                              <div className="flex gap-1">
                                <button
                                  onClick={() => updateStatus(issueId, 'Resolved', issue._underlyingIds)}
                                  className={cn(
                                    "px-2 py-1 rounded text-[10px] font-bold transition-colors",
                                    resState.status === 'Resolved'
                                      ? 'bg-success-500 text-white'
                                      : 'bg-neutral-100 text-neutral-400 hover:bg-success-50 hover:text-success-600'
                                  )}
                                >
                                  ✓
                                </button>
                                <button
                                  onClick={() => updateStatus(issueId, 'Escalated', issue._underlyingIds)}
                                  className={cn(
                                    "px-2 py-1 rounded text-[10px] font-bold transition-colors",
                                    resState.status === 'Escalated'
                                      ? 'bg-error-500 text-white'
                                      : 'bg-neutral-100 text-neutral-400 hover:bg-error-50 hover:text-error-600'
                                  )}
                                >
                                  ⚠
                                </button>
                              </div>
                            </div>

                            {issue.suggestedUPC && (
                              <div className="bg-primary-50 border-l-4 border-primary-400 pl-3 py-2 mb-2 text-xs">
                                <span className="font-semibold text-primary-700">Suggested UPC/Spot: </span>
                                <span className="text-primary-600">{issue.suggestedUPC}</span>
                              </div>
                            )}

                            <div className="grid grid-cols-2 gap-2 mt-3">
                              <input
                                type="text"
                                placeholder="Corrected UPC/Spot..."
                                className="text-xs border border-neutral-200 rounded px-2 py-1.5 outline-none focus:ring-2 focus:ring-primary-500"
                                value={resState.suggestedUPC || ''}
                                onChange={(e) => updateResolutionLocal(issueId, 'suggestedUPC', e.target.value)}
                                onBlur={(e) => commitResolution(issueId, 'suggestedUPC', e.target.value, issue._underlyingIds)}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                              />
                              <input
                                type="text"
                                placeholder="Resolution notes..."
                                className="text-xs border border-neutral-200 rounded px-2 py-1.5 outline-none focus:ring-2 focus:ring-primary-500"
                                value={resState.resolution || ''}
                                onChange={(e) => updateResolutionLocal(issueId, 'resolution', e.target.value)}
                                onBlur={(e) => commitResolution(issueId, 'resolution', e.target.value, issue._underlyingIds)}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            );
          })}
        </div>
      </main>
    </div>
  );
}

export default IssuesResolution;
